import { WebGPURenderer } from 'three/webgpu';
import { type Capabilities, requestedLimits } from '../core/capabilities';
import { quality } from '../core/settings';

/**
 * WebGPU-only renderer. Reversed-Z with a float depth buffer gives usable depth
 * precision from 5 cm to thousands of kilometres, which inside-out worlds need
 * because the far side of every hollow is on screen all the time.
 */
export async function createRenderer(canvas: HTMLCanvasElement, caps: Capabilities): Promise<WebGPURenderer> {
  const params = {
    canvas,
    antialias: false,
    reversedDepthBuffer: true,
    trackTimestamp: caps.timestampQuery,
    powerPreference: 'high-performance',
    requiredLimits: requestedLimits(caps),
  };
  const renderer = new WebGPURenderer(params as unknown as ConstructorParameters<typeof WebGPURenderer>[0]);
  await renderer.init();
  const backend = renderer.backend as unknown as { isWebGPUBackend?: boolean };
  if (!backend.isWebGPUBackend) {
    renderer.dispose();
    throw new Error('The WebGPU backend could not be initialised.');
  }
  applyRendererSize(renderer, canvas);
  window.addEventListener('resize', () => applyRendererSize(renderer, canvas));
  return renderer;
}

/** Extra render-scale factor set by dynamic resolution (1 = the preset's own scale). */
export const dynamicResolution = { scale: 1 };

export function applyRendererSize(renderer: WebGPURenderer, canvas: HTMLCanvasElement): void {
  if (canvas.clientWidth === 0 || canvas.clientHeight === 0) return;
  const q = quality();
  const ratio =
    Math.min(window.devicePixelRatio || 1, q.maxPixelRatio) * q.renderScale * dynamicResolution.scale;
  renderer.setPixelRatio(ratio);
  renderer.setSize(canvas.clientWidth || window.innerWidth, canvas.clientHeight || window.innerHeight, false);
}
